import { useState, useRef, useEffect } from 'react';
import { X, ChevronDown, Plus } from 'lucide-react';
import { Badge } from './ui/badge';

const TAG_COLORS = [
  'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
  'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300',
  'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300',
  'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300',
];

function getTagColor(name) {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return TAG_COLORS[Math.abs(hash) % TAG_COLORS.length];
}

export function TagChip({ name, onRemove, className = '' }) {
  const color = getTagColor(name);
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${color} ${className}`} data-testid={`tag-chip-${name.toLowerCase().replace(/\s+/g, '-')}`}>
      {name}
      {onRemove && (
        <button type="button" onClick={(e) => { e.stopPropagation(); onRemove(); }} className="hover:opacity-70 -mr-0.5">
          <X className="w-3 h-3" />
        </button>
      )}
    </span>
  );
}

export function TagChips({ tags = [], className = '' }) {
  if (!tags || tags.length === 0) return null;
  return (
    <span className={`inline-flex flex-wrap gap-1 ${className}`} data-testid="tag-chips">
      {tags.map((t) => (
        <TagChip key={t.id || t.name} name={t.name || t} />
      ))}
    </span>
  );
}

export default function TagMultiSelect({
  entityType = 'job',
  selectedTags = [],
  onChange,
  allTags = [],
  onCreateTag,
  placeholder = 'Search or create tags...',
  'data-testid': testId = 'tag-multi-select',
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selectedIds = new Set(selectedTags.map(t => t.id));
  const filtered = allTags.filter(t =>
    !selectedIds.has(t.id) && t.name.toLowerCase().includes(search.toLowerCase())
  );
  const exactMatch = allTags.some(t => t.name.toLowerCase() === search.trim().toLowerCase());
  const canCreate = search.trim() && !exactMatch;

  const handleSelect = (tag) => {
    onChange([...selectedTags, tag]);
    setSearch('');
    inputRef.current?.focus();
  };

  const handleRemove = (tagId) => {
    onChange(selectedTags.filter(t => t.id !== tagId));
  };

  const handleCreate = async () => {
    if (!canCreate) return;
    const newTag = await onCreateTag?.(search.trim(), entityType);
    if (newTag) {
      onChange([...selectedTags, newTag]);
      setSearch('');
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (canCreate) handleCreate();
      else if (filtered.length === 1) handleSelect(filtered[0]);
    }
    if (e.key === 'Backspace' && !search && selectedTags.length > 0) {
      handleRemove(selectedTags[selectedTags.length - 1].id);
    }
  };

  return (
    <div ref={containerRef} className="relative" data-testid={testId}>
      <div
        className="flex flex-wrap items-center gap-1 min-h-[36px] px-2 py-1 border rounded-md bg-background cursor-text focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-1"
        onClick={() => { setOpen(true); inputRef.current?.focus(); }}
      >
        {selectedTags.map(t => (
          <TagChip key={t.id} name={t.name} onRemove={() => handleRemove(t.id)} />
        ))}
        <input
          ref={inputRef}
          type="text"
          className="flex-1 min-w-[100px] bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          placeholder={selectedTags.length === 0 ? placeholder : ''}
          value={search}
          onChange={(e) => { setSearch(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          data-testid={`${testId}-input`}
        />
        <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
      </div>

      {open && (filtered.length > 0 || canCreate) && (
        <div className="absolute z-50 mt-1 w-full max-h-48 overflow-auto rounded-md border bg-popover shadow-md" data-testid={`${testId}-dropdown`}>
          {filtered.map(t => (
            <button
              key={t.id}
              type="button"
              className="w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-accent text-left"
              onClick={() => handleSelect(t)}
              data-testid={`tag-option-${t.name.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <TagChip name={t.name} className="pointer-events-none" />
              {t.usage_count !== undefined && <span className="text-xs text-muted-foreground ml-auto">{t.usage_count}</span>}
            </button>
          ))}
          {canCreate && (
            <button
              type="button"
              className="w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-accent text-left border-t"
              onClick={handleCreate}
              data-testid="tag-create-new"
            >
              <Plus className="w-3.5 h-3.5 text-primary" />
              <span>Create "<strong>{search.trim()}</strong>"</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
